import React, { useState, useMemo } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Typography,
  Skeleton,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material'
import {
  Search,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  Person,
  Email,
  Phone
} from '@mui/icons-material'

import { useClients, useDeleteClient } from '../../hooks/useClients'
import { formatCurrency, formatPhone, formatRUT } from '../../utils/formatters'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../Auth/RoleGuard'

const ClientsTable = ({ onEdit, onView }) => {
  const { isAdmin } = useAuthStore()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedClient, setSelectedClient] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const deleteClientMutation = useDeleteClient()

  // Query parameters
  const queryParams = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage,
    search: search.trim()
  }), [page, rowsPerPage, search])

  const { data, isLoading, error, refetch } = useClients(queryParams)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
    setPage(0)
  }

  const handleMenuOpen = (event, client) => {
    setAnchorEl(event.currentTarget)
    setSelectedClient(client)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedClient(null)
  }

  const handleEdit = () => {
    if (selectedClient && onEdit) {
      onEdit(selectedClient)
    }
    handleMenuClose()
  }

  const handleView = () => {
    if (selectedClient && onView) {
      onView(selectedClient)
    }
    handleMenuClose()
  }

  const handleDelete = () => {
    setDeleteConfirm(selectedClient)
    handleMenuClose()
  }

  const handleDeleteConfirm = async () => {
    if (deleteConfirm) {
      try {
        await deleteClientMutation.mutateAsync(deleteConfirm.id)
        refetch()
        setDeleteConfirm(null)
      } catch (error) {
        console.error('Error deleting client:', error)
      }
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirm(null)
  }

  const getStatusColor = (client) => {
    const debt = parseFloat(client.totalDebt || 0)
    if (debt === 0) return 'success'
    if (debt > 1000000) return 'error'
    return 'warning'
  }

  const getStatusLabel = (client) => {
    const debt = parseFloat(client.totalDebt || 0)
    if (debt === 0) return 'Al día'
    return 'Con deuda'
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton size="small" onClick={() => refetch()}>
            <Search />
          </IconButton>
        }
      >
        Error cargando clientes: {error.message}
      </Alert>
    )
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Search Bar */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar clientes por nombre, RUT o email..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Table */}
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell align="center">Plazo de Pago</TableCell>
              <TableCell align="center">Facturas Pendientes</TableCell>
              <TableCell align="right">Deuda Total</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box>
                        <Skeleton variant="text" width={120} />
                        <Skeleton variant="text" width={80} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} />
                    <Skeleton variant="text" width={80} />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" width={50} />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" width={40} />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" width={80} />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="rectangular" width={60} height={24} />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="circular" width={32} height={32} />
                  </TableCell>
                </TableRow>
              ))
            ) : data?.clients?.length > 0 ? (
              data.clients.map((client) => (
                <TableRow 
                  key={client.id} 
                  hover
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  {/* Cliente */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      >
                        {client.name.charAt(0).toUpperCase()}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {client.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatRUT(client.rut)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Contacto */}
                  <TableCell>
                    <Box>
                      {client.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="caption">
                            {client.email}
                          </Typography>
                        </Box>
                      )}
                      {client.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="caption">
                            {formatPhone(client.phone)}
                          </Typography>
                        </Box>
                      )}
                      {!client.email && !client.phone && (
                        <Typography variant="caption" color="text.secondary">
                          Sin contacto
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Plazo de Pago */}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {client.paymentTermsDays || 30}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        días
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Facturas Pendientes */}
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>
                      {client.pendingInvoicesCount || 0}
                    </Typography>
                  </TableCell>

                  {/* Deuda Total */}
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight={600}
                      color={parseFloat(client.totalDebt || 0) > 0 ? 'error.main' : 'text.primary'}
                    >
                      {formatCurrency(client.totalDebt || 0)}
                    </Typography>
                  </TableCell>

                  {/* Estado */}
                  <TableCell align="center">
                    <Chip
                      label={getStatusLabel(client)}
                      color={getStatusColor(client)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Acciones */}
                  <TableCell align="center">
                    <Tooltip title="Más opciones">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, client)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      No se encontraron clientes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search ? 'Intenta con otros términos de búsqueda' : 'Aún no hay clientes registrados'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {data && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.pagination?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ver detalles</ListItemText>
        </MenuItem>

        <RoleGuard requireAdmin fallback={null}>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>

          <MenuItem 
            onClick={handleDelete}
            disabled={deleteClientMutation.isLoading}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Eliminar</ListItemText>
          </MenuItem>
        </RoleGuard>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas <strong>eliminar permanentemente</strong> al cliente <strong>{deleteConfirm?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            ⚠️ Esta acción no se puede deshacer. Se eliminarán:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
            • El cliente y toda su información
            <br />
            • Todas sus facturas asociadas
            <br />
            • Historial completo de transacciones
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteClientMutation.isLoading}
          >
            {deleteClientMutation.isLoading ? 'Eliminando...' : 'Eliminar Permanentemente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default ClientsTable
